import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Trash2, UserPlus, ShieldCheck, BarChart3, ArrowUpCircle, Mail, Coins, Sparkles, Gauge } from "lucide-react";
import { Header } from "@/components/Header";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Team() {
  const { user, company, refresh } = useAuth();
  const navigate = useNavigate();
  const isOwner = user.role === "owner";
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [usage, setUsage] = useState(null);
  const [emailRows, setEmailRows] = useState([""]);
  const [role, setRole] = useState("sales_rep");
  const [sameDomain, setSameDomain] = useState(false);
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [u, i, us] = await Promise.all([api.get("/users"), api.get("/invites"), api.get("/usage")]);
    setUsers(u.data); setInvites(i.data); setUsage(us.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const checkout = async (lookup_key, quantity = 1) => {
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, quantity, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const [savingLimit, setSavingLimit] = useState(false);
  const [seatLimit, setSeatLimit] = useState(company.per_seat_daily_limit || 30);
  useEffect(() => { setSeatLimit(company.per_seat_daily_limit || 30); }, [company.per_seat_daily_limit]);

  const saveSeatLimit = async () => {
    setSavingLimit(true);
    try {
      await api.patch("/settings/seat-limit", { per_seat_daily_limit: Number(seatLimit) });
      await refresh();
      toast.success(`Each rep can now run ${seatLimit} generations per day.`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not update the limit.");
    } finally { setSavingLimit(false); }
  };

  const sendInvites = async () => {
    const list = emailRows.map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) return toast.error("Add at least one email");
    if (list.length > 10) return toast.error("Up to 10 at a time");
    setBusy(true);
    try {
      const { data } = await api.post("/invites", { emails: list, role, same_domain: sameDomain, domain });
      toast.success(data.email_enabled ? `${data.invites.length} invite email(s) sent` : `${data.invites.length} invite link(s) created`);
      setEmailRows([""]); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const setRow = (i, v) => setEmailRows((r) => r.map((x, idx) => (idx === i ? v : x)));
  const addRow = () => setEmailRows((r) => (r.length >= 10 ? r : [...r, ""]));
  const removeRow = (i) => setEmailRows((r) => (r.length === 1 ? [""] : r.filter((_, idx) => idx !== i)));

  const copyLink = (code) => { navigator.clipboard.writeText(`${window.location.origin}/invite/${code}`); toast.success("Invite link copied"); };
  const emailInvite = (inv) => {
    const link = `${window.location.origin}/invite/${inv.code}`;
    const subject = encodeURIComponent(`Join ${company.name} on Customize Studio`);
    const body = encodeURIComponent(`You're invited to join ${company.name} on Customize Studio as a ${inv.role.replace("_", " ")}.\n\nClick to set your password and get started:\n${link}\n`);
    window.location.href = `mailto:${inv.email}?subject=${subject}&body=${body}`;
  };
  const changeRole = async (id, newRole) => {
    try { await api.patch(`/users/${id}/role?role=${newRole}`); toast.success("Role updated"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const seatKey = "seat_standard";

  return (
    <div className="min-h-screen">
      <Header />
      <div className="mx-auto max-w-5xl px-6 py-10 md:px-10">
        <p className="overline">Manage</p>
        <h1 className="font-display text-3xl tracking-tight md:text-4xl">Your Team</h1>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard icon={ShieldCheck} label="Plan" value={company.subscription_active ? `${company.plan_name} · Active` : company.plan_name}
            hint="Unlimited team members"
            action={isOwner && !company.subscription_active && <MiniBtn onClick={() => checkout("plan_standard")} testid="subscribe-btn"><ArrowUpCircle className="h-3.5 w-3.5" /> Subscribe</MiniBtn>} />
          <StatCard icon={Coins} label="Credit balance" value={(company.credit_balance ?? 0).toLocaleString()}
            hint={`≈ ${Math.floor((company.credit_balance ?? 0) / (company.credits_per_generation || 10)).toLocaleString()} generations left`}
            action={isOwner && <MiniBtn onClick={() => navigate("/store")} testid="buy-credits-btn"><Sparkles className="h-3.5 w-3.5" /> Buy credits</MiniBtn>} />
          <StatCard icon={Gauge} label="Daily limit / rep" value={`${company.per_seat_daily_limit || 30}/day`}
            action={isOwner && (
              <div className="flex items-center gap-1.5">
                <input data-testid="seat-limit-input" type="number" min={1} max={1000} value={seatLimit}
                  onChange={(e) => setSeatLimit(e.target.value)}
                  className="w-16 rounded-lg border border-[var(--line)] bg-[var(--bone)] px-2 py-1 text-xs outline-none focus:border-[var(--brand)]" />
                <button data-testid="save-seat-limit-btn" onClick={saveSeatLimit} disabled={savingLimit}
                  className="rounded-full bg-[var(--ink)] px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50">Set</button>
              </div>
            )} />
        </div>

        {isOwner && (
          <div data-testid="topup-card" className="mt-4 flex flex-col items-start justify-between gap-3 rounded-xl border border-[var(--brand)]/30 bg-[var(--brand)]/[0.05] p-5 sm:flex-row sm:items-center">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--brand)]/10 text-[var(--brand)]"><Sparkles className="h-5 w-5" strokeWidth={1.75} /></span>
              <div>
                <p className="text-sm font-semibold">Running low on credits?</p>
                <p className="text-xs text-[var(--ink-soft)]">Top up anytime — credits roll over and never expire.</p>
              </div>
            </div>
            <button data-testid="go-to-store-btn" onClick={() => navigate("/store")}
              className="shrink-0 rounded-full bg-[var(--brand)] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--brand-dark)]">
              Open the Store
            </button>
          </div>
        )}

        {/* Usage tracking */}
        <div className="mt-8 rounded-xl border border-[var(--line)] bg-white p-6">
          <h2 className="flex items-center gap-2 text-sm font-semibold"><BarChart3 className="h-4 w-4 text-[var(--brand)]" /> Usage by user
            {usage && <span className="ml-2 rounded-full bg-[var(--surface-2)] px-2.5 py-0.5 text-xs font-medium text-[var(--ink-soft)]">{usage.total} total renders</span>}</h2>
          <div className="mt-4 hidden overflow-x-auto sm:block">
            <table className="w-full text-left text-sm">
              <thead className="text-[var(--ink-soft)]">
                <tr className="border-b border-[var(--line)]">
                  <th className="pb-2 font-medium">User</th><th className="pb-2 font-medium">Role</th>
                  <th className="pb-2 text-right font-medium">Today</th>
                  <th className="pb-2 text-right font-medium">Generates</th>
                  <th className="pb-2 text-right font-medium">Refines</th>
                  <th className="pb-2 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {usage?.per_user.map((r) => (
                  <tr key={r.user_id} data-testid={`usage-row-${r.user_id}`} className="border-b border-[var(--line)] last:border-0">
                    <td className="py-2.5"><span className="font-medium">{r.name || "—"}</span><span className="block text-xs text-[var(--ink-soft)]">{r.email}</span></td>
                    <td className="py-2.5 capitalize text-[var(--ink-soft)]">{String(r.role).replace("_", " ")}</td>
                    <td className="py-2.5 text-right">{r.today}/{r.daily_limit}</td>
                    <td className="py-2.5 text-right">{r.generate}</td>
                    <td className="py-2.5 text-right">{r.refine}</td>
                    <td className="py-2.5 text-right font-semibold">{r.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 space-y-3 sm:hidden">
            {usage?.per_user.map((r) => (
              <div key={r.user_id} data-testid={`usage-card-${r.user_id}`} className="rounded-xl border border-[var(--line)] p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{r.name || "—"}</p>
                    <p className="truncate text-xs text-[var(--ink-soft)]">{r.email}</p>
                  </div>
                  <span className="shrink-0 rounded-full bg-[var(--surface-2)] px-2.5 py-1 text-xs font-medium capitalize text-[var(--ink-soft)]">{String(r.role).replace("_", " ")}</span>
                </div>
                <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                  <div><p className="font-display text-lg">{r.today}<span className="text-xs text-[var(--ink-soft)]">/{r.daily_limit}</span></p><p className="text-[10px] uppercase text-[var(--ink-soft)]">Today</p></div>
                  <div><p className="font-display text-lg">{r.generate}</p><p className="text-[10px] uppercase text-[var(--ink-soft)]">Gen</p></div>
                  <div><p className="font-display text-lg">{r.refine}</p><p className="text-[10px] uppercase text-[var(--ink-soft)]">Refine</p></div>
                  <div><p className="font-display text-lg text-[var(--brand)]">{r.total}</p><p className="text-[10px] uppercase text-[var(--ink-soft)]">Total</p></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Invite */}
        <div className="mt-8 rounded-xl border border-[var(--line)] bg-white p-6">
          <h2 className="flex items-center gap-2 text-sm font-semibold"><UserPlus className="h-4 w-4 text-[var(--brand)]" /> Invite users (up to 10)</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1fr_220px]">
            <div className="space-y-2" data-testid="invite-boxes">
              {emailRows.map((val, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input data-testid={`invite-email-${i}`} value={val} onChange={(e) => setRow(i, e.target.value)}
                    placeholder={sameDomain ? "username (e.g. jane)" : "name@company.com"}
                    className="flex-1 rounded-lg border border-[var(--line)] bg-[var(--bone)] px-3 py-2.5 text-sm outline-none focus:border-[var(--brand)]" />
                  <button data-testid={`remove-email-${i}`} onClick={() => removeRow(i)} title="Remove"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[var(--line)] text-[var(--ink-soft)] hover:border-red-300 hover:bg-red-50 hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {emailRows.length < 10 && (
                <button data-testid="add-email-btn" onClick={addRow}
                  className="flex items-center gap-1.5 rounded-lg px-1 py-1 text-sm font-medium text-[var(--brand)] hover:underline">
                  <UserPlus className="h-4 w-4" /> Add another
                </button>
              )}
            </div>
            <div className="space-y-3">
              <div><span className="overline">Role</span>
                <select data-testid="invite-role" value={role} onChange={(e) => setRole(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-[var(--line)] bg-[var(--bone)] px-3 py-2.5 text-sm outline-none focus:border-[var(--brand)]">
                  <option value="sales_rep">Sales Rep</option><option value="manager">Manager</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm" data-testid="same-domain-toggle">
                <input type="checkbox" checked={sameDomain} onChange={(e) => setSameDomain(e.target.checked)} /> All users share the same domain
              </label>
              {sameDomain && <input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="company.com" data-testid="invite-domain"
                className="w-full rounded-lg border border-[var(--line)] bg-[var(--bone)] px-3 py-2 text-sm outline-none focus:border-[var(--brand)]" />}
              <button data-testid="send-invites-btn" onClick={sendInvites} disabled={busy}
                className="w-full rounded-full bg-[var(--brand)] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--brand-dark)] disabled:opacity-60">
                {busy ? "Sending…" : "Send invites"}
              </button>
            </div>
          </div>
          {invites.length > 0 && (
            <div className="mt-5">
              <p className="overline mb-2">Pending invites</p>
              <div className="space-y-2">
                {invites.map((inv) => (
                  <div key={inv.id} data-testid={`invite-row-${inv.id}`} className="flex items-center justify-between rounded-lg border border-[var(--line)] px-3 py-2 text-sm">
                    <span>{inv.email} <span className="text-[var(--ink-soft)]">· {inv.role.replace("_", " ")}</span></span>
                    <div className="flex items-center gap-1">
                      <MiniBtn onClick={() => emailInvite(inv)} testid={`email-${inv.id}`}><Mail className="h-3.5 w-3.5" /> Email</MiniBtn>
                      <MiniBtn onClick={() => copyLink(inv.code)} testid={`copy-${inv.id}`}><Copy className="h-3.5 w-3.5" /> Copy link</MiniBtn>
                      <button onClick={() => api.delete(`/invites/${inv.id}`).then(load)} className="rounded p-1.5 text-[var(--brand)] hover:bg-[var(--surface-2)]"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Members */}
        <h2 className="mt-8 text-sm font-semibold">Members ({users.length})</h2>
        <div className="mt-3 overflow-hidden rounded-xl border border-[var(--line)] bg-white">
          {users.map((u) => (
            <div key={u.id} data-testid={`member-${u.id}`} className="flex items-center justify-between border-b border-[var(--line)] px-4 py-3 last:border-0">
              <div>
                <p className="text-sm font-medium">{u.name} {u.id === user.id && <span className="text-xs text-[var(--ink-soft)]">(you)</span>}</p>
                <p className="text-xs text-[var(--ink-soft)]">{u.email} · {u.gen_used}/{u.daily_limit} today</p>
              </div>
              {u.role === "owner" ? <span className="rounded-full bg-[var(--ink)] px-3 py-1 text-xs font-medium text-white">Owner</span>
                : isOwner ? (
                  <select value={u.role} onChange={(e) => changeRole(u.id, e.target.value)} data-testid={`role-${u.id}`}
                    className="rounded-lg border border-[var(--line)] bg-[var(--bone)] px-2 py-1.5 text-xs outline-none">
                    <option value="sales_rep">Sales Rep</option><option value="manager">Manager</option>
                  </select>
                ) : <span className="text-xs text-[var(--ink-soft)] capitalize">{u.role.replace("_", " ")}</span>}
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-[var(--ink-soft)]">Each generation costs {company.credits_per_generation || 10} credits from your shared balance. Every rep can run up to {company.per_seat_daily_limit || 30} generations/day (resets daily) — the owner can change this above. Credits roll over and never expire. Payments are processed securely by Stripe.</p>
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, action, hint }) => (
  <div className="rounded-xl border border-[var(--line)] bg-white p-4">
    <div className="flex items-center justify-between"><span className="overline">{label}</span><Icon className="h-4 w-4 text-[var(--brand)]" strokeWidth={1.5} /></div>
    <div className="mt-1 flex items-end justify-between"><span className="font-display text-2xl">{value}</span>{action}</div>
    {hint && <p className="mt-1 text-xs text-[var(--ink-soft)]">{hint}</p>}
  </div>
);
const MiniBtn = ({ children, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className="flex items-center gap-1 rounded-full border border-[var(--line)] px-2.5 py-1 text-xs font-medium transition-colors hover:bg-[var(--surface-2)]">{children}</button>
);
